import React from 'react';
import PropTypes from 'prop-types';
import BaseComponent from "core/gd-components/base-component/base-component";

import HintPopover from '../../hint-popover/hint-popover';
import InputInlineEditable from "core/gd-components/input-inline-editable/input-inline-editable";
import EstimateAllocateUtils from "src/core/gd/utils/estimate-allocate-utils";
import EstimatePickerPopover from "../popover/ui5/popover-ui5";
import ControlButtonStandard from "../../../gd-components/buttons/control-button/control-button-standard"

export default class EstimatePicker extends BaseComponent {

    static propTypes = {
        value: PropTypes.number,
        childrenValue: PropTypes.number,
        showSummary: PropTypes.bool,

        placeholder: PropTypes.string,
        hint: PropTypes.string,
        disabled: PropTypes.bool,

        // form - if passed component will act as form element
        form: PropTypes.object,
        name: PropTypes.string,

        // underline
        underlined: PropTypes.oneOf(['on-hover', 'always', 'never','none']),  // if set will show border bottom for the value

        // look and feel
        // text 13 = text normal text-12 = text small , 11 -> tiny
        renderValue: PropTypes.oneOf(['text-normal','text-13', 'text-12', 'text-11', 'inline', 'form-element', 'form-input-hours', 'control-button', 'func']).isRequired, // render value view name
        renderValueFunc: PropTypes.func,        // func for external render function if passwed used to render instead of default

        deleteButton: PropTypes.bool,        // if true will show delte button to reset date to null

        // popover options
        attachment: PropTypes.string,
        targetAttachment: PropTypes.string,
        offset: PropTypes.string,

        // Callbacks
        onUserSelect: PropTypes.func,
        onTogglePopover: PropTypes.func, // to track if any popover opened from this component
    };

    static defaultProps =  {
        placeholder: "Estimate",

        deleteButton: false,

        attachment: "bottom center",
        targetAttachment: "top center"
    };

    constructor(props) {
        super(props);

        if (this.props.form) this.props.form.registerFormComponent(this.props.name, this);

        const value = this.props.form ? this.props.form.getValue(this.props.name) : this.props.value;
        const hours = value ? Math.floor(value / 60) : 0;
        const minutes = value ? value % 60 : 0;

        this.state = {
            showPopover: false,
            hours: hours,
            minutes: minutes
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps){
        // when form value changed and we re-render the whole thing
        if (nextProps.form) {
            const formValue = nextProps.form.getValue(nextProps.name);
            if (formValue !== this._getValue()) {
                this._setValue(formValue);
            }
        } else {
            if (typeof nextProps.value !== 'undefined' && nextProps.value !== this._getValue()) {
                this._setValue(nextProps.value);
            }
        }
    }

    _getValue = () => this.state.hours * 60 + this.state.minutes

    _getRenderValue = () => {
        const value = this._getValue();
        return this.props.showSummary ? this.props.childrenValue + (value ? value : 0) : value
    }

    _setValue = (value) => {
        this.setState({
            hours: value ? Math.floor(value / 60) : 0,
            minutes: value ? value % 60 : 0
        });
    }

    blur = () => {
        if (this.formInput) this.formInput.blur();
    }

    togglePopover = () => {
        if (!this.props.disabled) {
            this.setState({showPopover: !this.state.showPopover},
                ()=>{if (this.props.onTogglePopover) this.props.onTogglePopover(this.state.showPopover)});
        }
    }

    hidePopover = () => {
        this.setState({showPopover: false},
            ()=>{if (this.props.onTogglePopover) this.props.onTogglePopover(this.state.showPopover)});
    }

    onDeleteClick = () => {
        if (this.props.onUserSelect) this.props.onUserSelect(false);
        if (this.props.form) this.props.form.onFormComponentValueChange(this.props.name,null);

        this.hidePopover();
    }

    onFormInputHoursValueChange = (value) => {
        this.onSave(EstimateAllocateUtils.normalizeStringInputToMinutes(value));
    }

    onFormInputHoursValueImmediateChange = (value) => {
        if (this.formInput) this.toggleClass("no-value", !value, this.formInput);
    }

    onSubmit = (result) => {
        let estimate = result.hours * 60 + result.minutes;
        if (estimate === 0) estimate = null;

        if (this.props.onUserSelect) this.props.onUserSelect(estimate);
        if (this.props.form) this.props.form.onFormComponentValueChange(this.props.name, estimate);

        this.hidePopover();
    }

    onSave = (value) => {
        const hours = value ? Math.floor(value / 60) : 0;
        const minutes = value ? value % 60 : 0;

        this.setState({
            hours: hours,
            minutes: minutes
        }, ()=>{
            const newEstimateValue = this._getValue();
            if (this.props.onUserSelect) this.props.onUserSelect(newEstimateValue);
            if (this.props.form) this.props.form.onFormComponentValueChange(this.props.name,newEstimateValue);
            this.hidePopover();
        });
    }

    renderPopover() {
        if (this.state.showPopover) return <EstimatePickerPopover hours={this.state.hours}
                                                                  minutes={this.state.minutes}
                                                                  childrenValue={this.props.childrenValue}
                                                                  attachment={this.props.attachment}
                                                                  targetAttachment={this.props.targetAttachment}
                                                                  offset={this.props.offset}
                                                                  onClickOutside={()=>{this.hidePopover()}}
                                                                  onSave={this.onSave}/>

        return null;
    }

    renderValue(value) {
        let valueLabel = this.props.placeholder;
        if (value) valueLabel = gd.utils.minutesHumanize(value);

        const underlined = (this.props.disabled) ? 'underlined-never' : 'underlined-'+this.props.underlined;

        const curValueClasses = Classes('cur-value','pop-target', this.props.renderValue, underlined, {
            'no-value': !value
        });

        switch (this.props.renderValue) {
            case 'form-element':
            case 'text-normal':
            case 'text-13':
            case 'text-12':
            case 'text-11':
                return (
                    <div className={curValueClasses} onClick={this.togglePopover}>
                        {valueLabel}
                    </div>
                );

            case 'inline':
                let res2 = valueLabel;
                if (!value) res2 = <div className="icon i5-time-estimate"/>;

                return (
                    <div className={Classes(curValueClasses, "ui5-circle-selector")} onClick={this.togglePopover}>
                        {res2}
                    </div>
                )

            case 'form-input-hours':
                const hours = EstimateAllocateUtils.minutesToHours(value);
                return <InputInlineEditable ref={r=>this.formInput = r}
                                            className={curValueClasses}
                                            placeholder="0"
                                            value={hours > 0 ? hours.toString() : null}
                                            type="number"
                                            disabled={this.props.disabled}
                                            onChange={this.onFormInputHoursValueChange}
                                            onImmediateChange={this.onFormInputHoursValueImmediateChange}
                                            autoSaveChanges={false}
                                            autoSaveOnBlur={true}/>;

            case 'control-button':
                return <ControlButtonStandard className     = "pop-target"
                                              iconClass     = "i5-time-estimate"
                                              isDisabled    = {this.props.disabled}
                                              onClick       = {this.togglePopover}/>

            case 'func':
                return (
                    <div className={curValueClasses} onClick={this.togglePopover}>
                        {this.props.renderValueFunc(value)}
                    </div>
                );
        }
    }

    render() {
        const value = this._getRenderValue();

        const classes = Classes("gd-select-estimate", this.props.className, 'render-'+this.props.renderValue, {
            'disabled': this.props.disabled,
            'not-selected': !value,
            'open': this.state.showPopover,
        });

        return (
            <div className={classes}>
                {!!value && this.props.showSummary && <div className="estimate-icon ui5-ico-12 i5-summary-symbol m-r-5"/>}
                {this.renderValue(value)}
                {!this.props.disabled && this.props.deleteButton && <div className="i5-delete-circle-solid" onClick={this.onDeleteClick}/>}
                {this.renderPopover()}
                {this.props.hint && !this.state.showPopover && <HintPopover parent={this} hint={this.props.hint}/>}
            </div>
        );
    }
}